import React, { useEffect, useRef } from "react";
import { SectionTrigger, SectionIcon } from "../../orderManagement/molecules/SectionTrigger";
import { getSectionStatus } from "./sectionConfig";
import { getQuoteChecklistItems } from "../components/molecules/selectQuoteChecklistItems";

/**
 * The flow's progress rail (Alex, 2026-08-21 redesign).
 *
 * Desktop (≥1280px): a connected stepper — progress header (flow badge, fill
 * bar, done count), a rail line that fills to the current position, buy/sell
 * tags on quote steps, trade-ins grouped under a labelled divider, done steps
 * carrying a one-line recap, and the current SELECT QUOTE step showing a
 * compact mirror of the "Before you continue" checklist (same helper, so the
 * rail and the section card can never disagree).
 *
 * Mobile (<1280px): the same DOM collapses via CSS into a slim top bar —
 * marker dots + the progress bar, with a "Step X of N · <current>" caption
 * (rendered here, hidden on desktop). Labels/subtitles/checks hide.
 *
 * Step state is DATA-driven (getSectionStatus reads the request), never visit
 * order — skipping a section and completing a later one renders truthfully.
 */

const LockIcon = () => (
  <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

// One-line recap for a completed step, from the section's own card data —
// the same source the smart cards render, so the rail never invents state.
function doneSubtitle(section, state) {
  try {
    const fields = section.getCardData?.(state)?.fields ?? [];
    // Prefer the first HUMAN field — raw ids ("Product ID: 9001") recap
    // nothing a rep recognises; brand/client/price do.
    const field =
      fields.find(
        (f) => f.value != null && f.value !== "" && !/\bid$/i.test(f.label ?? "")
      ) ?? fields.find((f) => f.value != null && f.value !== "");
    if (!field) return null;
    const text = String(field.value);
    return text.length > 34 ? `${text.slice(0, 33)}…` : text;
  } catch (e) {
    return null;
  }
}

export function RequestSectionNav({
  sections,
  selectedSectionId,
  onSelect,
  state,
}) {
  const rows = sections.map((section) => ({
    section,
    status: getSectionStatus(section, state),
  }));

  const total = rows.length;
  const doneCount = rows.filter((r) => r.status.kind === "complete").length;
  const currentIdx = rows.findIndex((r) => r.section.id === selectedSectionId);
  const currentRow = currentIdx >= 0 ? rows[currentIdx] : null;

  const isPx = !!state?.isPxFlow || rows.some((r) => r.section.pxSellIdx != null);
  const requestType = state?.requestType ?? state?.request?.request_type;
  const flowBadge = isPx
    ? "Part Exchange"
    : requestType === "sell"
      ? "Sell"
      : requestType === "order"
        ? "Order"
        : null;

  // Trade-ins group: the divider sits before the FIRST px-sell slot section.
  const firstPxIdx = rows.findIndex((r) => r.section.pxSellIdx != null);
  const pxCount = rows.reduce(
    (mx, r) => (r.section.pxSellIdx != null ? Math.max(mx, r.section.pxSellIdx + 1) : mx),
    0
  );

  const quoteTag = (section) => {
    if (section.id === "send_quote") {
      return requestType === "sell" ? "sell" : "buy";
    }
    if (/^send_quote_sell_\d+$/.test(section.id)) return "sell";
    return null;
  };

  // Compact mirror of the section's "Before you continue" items — main quote
  // step only (a trade-in slot's own request isn't in nav state).
  const miniChecks =
    currentRow?.section?.id === "send_quote" && state?.request
      ? getQuoteChecklistItems(state.request, null, false)
      : null;

  const progressPct = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  // Mobile dot bar: keep the current step's marker in view — the bar scrolls
  // horizontally and a deep step would otherwise sit off-screen. Inline-axis
  // only, so the desktop rail (vertical, no horizontal overflow) is a no-op.
  const selectedRef = useRef(null);
  useEffect(() => {
    selectedRef.current?.scrollIntoView?.({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedSectionId]);

  return (
    <nav className="rc-request-v2__nav" aria-label="Request sections">
      <div className="rc-request-v2__nav-head">
        <div className="rc-request-v2__nav-head-row">
          <span className="rc-request-v2__nav-title">Progress</span>
          {flowBadge && (
            <span className="rc-request-v2__nav-flow">{flowBadge}</span>
          )}
        </div>
        <div
          className="rc-request-v2__nav-bar"
          role="progressbar"
          aria-valuenow={doneCount}
          aria-valuemin={0}
          aria-valuemax={total}
        >
          <i style={{ width: `${progressPct}%` }} />
        </div>
        <div className="rc-request-v2__nav-count">
          <b>{doneCount}</b> of {total} steps done
        </div>
      </div>

      <ul className="rc-request-v2__nav-list">
        {rows.map(({ section, status }, idx) => {
          const isSelected = section.id === selectedSectionId;
          const isLocked = status.kind === "locked";
          const tag = quoteTag(section);
          const subtitle =
            status.kind === "complete" ? doneSubtitle(section, state) : null;
          const className = [
            "rc-request-v2__nav-item",
            isSelected && "rc-request-v2__nav-item--selected",
            `rc-request-v2__nav-item--${status.kind}`,
            section.isSubItem && "rc-request-v2__nav-item--sub",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <React.Fragment key={section.id}>
              {idx === firstPxIdx && pxCount > 0 && (
                <li className="rc-request-v2__nav-group" aria-hidden>
                  Trade-ins · {pxCount}
                </li>
              )}
              <li className="rc-request-v2__nav-row">
                <button
                  type="button"
                  className={className}
                  onClick={() => onSelect(section.id)}
                  title={isLocked ? status.message : undefined}
                  ref={isSelected ? selectedRef : undefined}
                >
                  {/* Mobile only (CSS): the section's own icon above the status
                      marker, same ICONS map as the desktop labels — payments is
                      always the card, search always the magnifier. */}
                  <span className="rc-request-v2__nav-ico" aria-hidden>
                    <SectionIcon icon={section.icon} />
                  </span>
                  <span className="rc-request-v2__nav-status">
                    {status.kind === "complete" && (
                      <span className="rc-request-v2__nav-tick" aria-label="Complete">
                        ✓
                      </span>
                    )}
                    {status.kind === "pending" &&
                      (isSelected ? (
                        <span className="rc-request-v2__nav-ring" aria-label="Current">
                          <i />
                        </span>
                      ) : (
                        <span className="rc-request-v2__nav-dot" aria-label="To do" />
                      ))}
                    {status.kind === "locked" && (
                      <span className="rc-request-v2__nav-lock" aria-label="Locked">
                        <LockIcon />
                      </span>
                    )}
                  </span>
                  <span className="rc-request-v2__nav-body">
                    <span className="rc-request-v2__nav-label">
                      <SectionTrigger
                        label={section.navLabel ?? section.label}
                        icon={section.icon}
                      />
                      {tag && (
                        <span className={`rc-request-v2__nav-tag rc-request-v2__nav-tag--${tag}`}>
                          {tag === "buy" ? "Buy" : "Sell"}
                        </span>
                      )}
                    </span>
                    {subtitle && (
                      <span className="rc-request-v2__nav-sub">{subtitle}</span>
                    )}
                    {isSelected && miniChecks && (
                      <span className="rc-request-v2__nav-checks">
                        {miniChecks.map((item) => (
                          <span
                            key={item.key}
                            className={`rc-request-v2__nav-check${item.done ? " is-done" : ""}`}
                          >
                            <i aria-hidden>{item.done ? "✓" : ""}</i>
                            {item.label}
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            </React.Fragment>
          );
        })}
      </ul>

      {/* Mobile top-bar caption (design 5) — hidden on desktop via CSS. */}
      <div className="rc-request-v2__nav-caption">
        Step {currentIdx + 1} of {total}
        {currentRow && (
          <>
            {" · "}
            <b>{currentRow.section.navLabel ?? currentRow.section.label}</b>
          </>
        )}
      </div>
    </nav>
  );
}
